import type { StateCreator } from 'zustand';
import type { SliceMutators } from '~/store/mutators';
import type { RootState } from '~/store/slices';
import type { BgColor, KeyedRegion } from '~/types/pipeline';
import { type BrushStroke, subtractStrokeFromRegions } from './brushStroke';
import { floodFromSeed } from './keyRegion';

export type BackgroundSlice = {
  background: {
    enabled: boolean;
    tolerance: number;
    wandTolerance: number;
    brushRadius: number;
    brushSoftness: number;
    hardEdge: boolean;
    erode: number;
    feather: number;
    despillStrength: number;
    despillRadius: number;
    floodFill: boolean;
    maxEnclosedIsland: number;
    bgColors: BgColor[];
    regions: KeyedRegion[];
    /** Output of the watermark→bg-removal pipeline; written by useCleanedPipeline. */
    cleaned: ImageData | null;
  };
  setBackgroundEnabled: (b: boolean) => void;
  setTolerance: (n: number) => void;
  setWandTolerance: (n: number) => void;
  setBrushRadius: (n: number) => void;
  setBrushSoftness: (n: number) => void;
  setHardEdge: (b: boolean) => void;
  setErode: (n: number) => void;
  setFeather: (n: number) => void;
  setDespillStrength: (n: number) => void;
  setDespillRadius: (n: number) => void;
  setFloodFill: (b: boolean) => void;
  setMaxEnclosedIsland: (n: number) => void;
  setBgColors: (cs: BgColor[]) => void;
  setCleaned: (i: ImageData | null) => void;
  addRegion: (region: KeyedRegion) => void;
  removeRegion: (id: string) => void;
  clearRegions: () => void;
  subtractStroke: (stroke: BrushStroke) => void;
  setRegionTolerance: (id: string, tol: number) => void;
  resetBackground: () => void;
};

const INITIAL_BACKGROUND = {
  enabled: true,
  tolerance: 24,
  wandTolerance: 48,
  brushRadius: 8,
  brushSoftness: 0.5,
  hardEdge: false,
  erode: 0,
  feather: 0,
  despillStrength: 0,
  despillRadius: 0,
  floodFill: false,
  maxEnclosedIsland: 0,
  bgColors: [],
  regions: [],
  cleaned: null,
} satisfies BackgroundSlice['background'];

export const createBackgroundSlice: StateCreator<RootState, SliceMutators, [], BackgroundSlice> = (
  set,
  get,
) => ({
  background: INITIAL_BACKGROUND,
  setBackgroundEnabled: (b) =>
    set((s) => {
      s.background.enabled = b;
    }),
  setTolerance: (n) =>
    set((s) => {
      s.background.tolerance = n;
    }),
  setWandTolerance: (n) =>
    set((s) => {
      if (s.background.wandTolerance === n) return;
      s.background.wandTolerance = n;
    }),
  setBrushRadius: (n) =>
    set((s) => {
      if (s.background.brushRadius === n) return;
      s.background.brushRadius = n;
    }),
  setBrushSoftness: (n) =>
    set((s) => {
      if (s.background.brushSoftness === n) return;
      s.background.brushSoftness = n;
    }),
  setHardEdge: (b) =>
    set((s) => {
      s.background.hardEdge = b;
    }),
  setErode: (n) =>
    set((s) => {
      if (s.background.erode === n) return;
      s.background.erode = n;
    }),
  setFeather: (n) =>
    set((s) => {
      if (s.background.feather === n) return;
      s.background.feather = n;
    }),
  setDespillStrength: (n) =>
    set((s) => {
      if (s.background.despillStrength === n) return;
      s.background.despillStrength = n;
    }),
  setDespillRadius: (n) =>
    set((s) => {
      if (s.background.despillRadius === n) return;
      s.background.despillRadius = n;
    }),
  setFloodFill: (b) =>
    set((s) => {
      if (s.background.floodFill === b) return;
      s.background.floodFill = b;
    }),
  setMaxEnclosedIsland: (n) =>
    set((s) => {
      if (s.background.maxEnclosedIsland === n) return;
      s.background.maxEnclosedIsland = n;
    }),
  setBgColors: (cs) =>
    set((s: BackgroundSlice) => {
      s.background.bgColors = cs;
    }),
  setCleaned: (i) =>
    set((s: BackgroundSlice) => {
      if (s.background.cleaned === i) return;
      s.background.cleaned = i;
    }),
  addRegion: (region) =>
    set((s) => {
      s.background.regions.push(region);
    }),
  removeRegion: (id) =>
    set((s) => {
      s.background.regions = s.background.regions.filter((r) => r.id !== id);
    }),
  clearRegions: () =>
    set((s) => {
      if (s.background.regions.length === 0) return;
      s.background.regions = [];
    }),
  subtractStroke: (stroke) =>
    set((s) => {
      const next = subtractStrokeFromRegions(s.background.regions, stroke);
      if (next === s.background.regions) return;
      s.background.regions = next;
    }),
  setRegionTolerance: (id, tol) => {
    const { source, background } = get();
    const { imageData } = source;
    if (!imageData) return;
    const idx = background.regions.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const existing = background.regions[idx];
    if (!existing) return;
    if (existing.tolerance === tol) return;
    const next = floodFromSeed(
      imageData,
      background.bgColors,
      existing.seed,
      tol,
      background.hardEdge,
      background.cleaned,
      existing.id,
    );
    if (!next) return;
    set((s) => {
      const i = s.background.regions.findIndex((r) => r.id === id);
      if (i !== -1) s.background.regions[i] = next;
    });
  },
  resetBackground: () =>
    set((s) => {
      s.background = { ...INITIAL_BACKGROUND };
    }),
});
